import { Key, ReactElement, ReactNode, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, IconButton, Menu, MenuItem } from '@mui/material';
import { GridLogicOperator, useGridApiContext } from '@mui/x-data-grid-pro';
import {
  filterLineContentSx,
  filterLineRemoveColumnSx,
  filterLineSx,
  filterSingleNodeChildrenSx,
  filterTreeChildrenSx,
  type FilterTreeNodeKind,
  getFilterTreeNodeSx,
  logicOperatorPillSx,
} from './styles';

const LOGIC_OPERATORS = [GridLogicOperator.And, GridLogicOperator.Or] as const;

const LOCALE_KEY_OF_LOGIC_OPERATOR = {
  [GridLogicOperator.And]: 'filterPanelOperatorAnd',
  [GridLogicOperator.Or]: 'filterPanelOperatorOr',
} as const;

/** One node of a group, with its connector to the group's line. */
export function FilterTreeNode({
  kind = 'row',
  children,
}: {
  kind?: FilterTreeNodeKind;
  children: ReactNode;
}): ReactElement {
  return <Box sx={getFilterTreeNodeSx(kind)}>{children}</Box>;
}

/** A node of a group: a row or a group of rows. */
export interface FilterTreeItem {
  /** The row's id or the group's; a row restored without an id has none, as MUI allows for a single row. */
  key: Key | undefined;
  kind: FilterTreeNodeKind;
  content: ReactNode;
}

interface FilterTreeChildrenProps {
  logicOperator: GridLogicOperator;
  onChangeLogicOperator: (logicOperator: GridLogicOperator) => void;
  items: FilterTreeItem[];
}

/**
 * The nodes of a group, indented under it and joined by a line, with the
 * group's one AND / OR on that line between them; a single node has nothing
 * to join.
 */
export function FilterTreeChildren({ logicOperator, onChangeLogicOperator, items }: FilterTreeChildrenProps): ReactElement {
  const hasNodesToJoin = items.length > 1;
  return (
    <Box sx={hasNodesToJoin ? filterTreeChildrenSx : filterSingleNodeChildrenSx}>
      {hasNodesToJoin && <LogicOperatorPill logicOperator={logicOperator} onChange={onChangeLogicOperator} />}
      {items.map(({ key, kind, content }) => (
        <FilterTreeNode key={key} kind={kind}>
          {content}
        </FilterTreeNode>
      ))}
    </Box>
  );
}

interface FilterLineProps {
  onRemove: () => void;
  children: ReactNode;
}

/** A filter row: a card with the row's inputs and the remove button at the end. */
export function FilterLine({ onRemove, children }: FilterLineProps): ReactElement {
  const apiRef = useGridApiContext();
  const removeLabel = apiRef.current.getLocaleText('filterPanelDeleteIconLabel');

  return (
    <Box sx={filterLineSx}>
      <Box sx={filterLineContentSx}>{children}</Box>
      <Box sx={filterLineRemoveColumnSx}>
        <IconButton size='small' aria-label={removeLabel} title={removeLabel} onClick={onRemove}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
    </Box>
  );
}

interface LogicOperatorPillProps {
  logicOperator: GridLogicOperator;
  onChange: (logicOperator: GridLogicOperator) => void;
}

/** The AND / OR of a group as a pill on the group's line; it opens a menu to change it. */
export function LogicOperatorPill({ logicOperator, onChange }: LogicOperatorPillProps): ReactElement {
  const apiRef = useGridApiContext();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const labelOf = (operator: GridLogicOperator) => apiRef.current.getLocaleText(LOCALE_KEY_OF_LOGIC_OPERATOR[operator]);

  return (
    <>
      <Button
        size='small'
        sx={logicOperatorPillSx}
        endIcon={<ExpandMoreIcon fontSize='small' />}
        aria-haspopup='menu'
        aria-expanded={Boolean(anchor)}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        {labelOf(logicOperator)}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {LOGIC_OPERATORS.map((operator) => (
          <MenuItem
            key={operator}
            selected={operator === logicOperator}
            onClick={() => {
              setAnchor(null);
              onChange(operator);
            }}
          >
            {labelOf(operator)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
